import { Zap, ChevronRight } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/common/utils/formatNumber';
import { formatDateTime } from '@/common/utils/formatDateTime';
import { getActivePrice } from '../../utils/prices';
import { ProductStatusBadge } from '../product-status-badge/ProductStatusBadge';
import type { Product, Variant } from '../../services/schemas';

interface ActiveProductCardProps {
  product: Product;
  variant: Variant;
  onClick: () => void;
}

export function ActiveProductCard({ product, variant, onClick }: ActiveProductCardProps) {
  const price = getActivePrice(variant.priceSet);
  const isMonthly = product.billingFrequency === 'monthly';
  const amount = price ? (isMonthly ? price.priceMonthly : price.priceAnnual) : null;
  const period = isMonthly ? <Trans>mo</Trans> : <Trans>yr</Trans>;
  const nextBilling = price?.validTo ? formatDateTime(new Date(price.validTo), 'date') : null;

  return (
    <button
      onClick={onClick}
      className={cn(
        'text-left w-full bg-white border border-gray-200 rounded-2xl p-4',
        'hover:border-gray-300 transition-all group flex items-start gap-4'
      )}
    >
      <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-blue-50">
        <Zap size={18} className="text-blue-600" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {variant.productType.name}
          </span>
          <ProductStatusBadge status={product.status} />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {amount !== null && price && (
            <span className="text-xs font-semibold text-gray-800">
              {formatNumber(amount, 'currency', { currency: 'CZK' })}
              <span className="text-xs font-normal text-gray-400"> / {period}</span>
            </span>
          )}

          <span className="text-gray-200">·</span>

          <span className="text-xs text-gray-500">
            {isMonthly ? <Trans>Monthly</Trans> : <Trans>Annual</Trans>}
          </span>

          {nextBilling && (
            <>
              <span className="text-gray-200">·</span>
              <span className="text-xs text-gray-400">
                <Trans>Until {nextBilling}</Trans>
              </span>
            </>
          )}
        </div>
      </div>

      <ChevronRight
        size={16}
        className="text-gray-300 group-hover:text-gray-500 transition-colors shrink-0 mt-1"
      />
    </button>
  );
}
